import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {type Action, openConfirmDialog, type TabProps, Tabs} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  type CommonDescriptionAttribute,
  useGetGeneralSettingsQuery,
  useGetSalesPlatformsQuery,
  useGetVehicleQuery,
  useRenderWindshieldPaperDocumentMutation,
  useUpdateVehicleIsInSaleMutation,
  useUpdateVehicleOnAllPlatformsMutation,
} from '@dms/api/sales';
import {useValidateMagicPublishingMutation} from '@dms/api/settings';
import {type AdvertisingPlatformCodeEnum, type ApiError, EntityResourceIds} from '@dms/api/shared';
import {ConditionEnumSchema} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  getFieldAvailabilityContextFromVehicle,
  handleApiError,
  hasAclDocsWithPermission,
  printPDF,
  PkwEnvkvAdvertisingNotice,
  queryParams,
  useDocumentTemplates,
  useIsLicenceEnabled,
  useMandatoryFields,
  useMandatoryFieldsErrorDialog,
  usePermissions,
  usePkwEnvkvAdvertisingPreflight,
} from '@dms/shared';

import {buildArray, openInNewTab, useQueryState, useRequiredParams} from 'shared/web';

import {useAdvertisementPolling} from '../contexts/AdvertisementPollingContext';
import {useAdvertisementPreviewDialog} from '../hooks/useAdvertisementPreviewDialog';
import {useAdvertisementPreviewQueryArgs} from '../hooks/useAdvertisementPreviewQueryArgs';
import {useConfirmUnsavedGeneralChangesBeforeOpen} from '../hooks/useConfirmUnsavedGeneralChangesBeforeOpen';
import {formatOverLimitWarnings} from '../utils/formatOverLimitWarnings';
import {getOverLimitPlatforms} from '../utils/getOverLimitPlatforms';
import {openMagicPublishingDialog} from '../utils/openMagicPublishingDialog';
import {AdvertisementPreviewDialog} from './AdvertisementPreviewDialog';
import {AdvertisementTab} from './AdvertisementTab';
import {WindshieldTab} from './WindshieldTab';

const TAB_IDS = {
  AD_PLATFORMS: 'adPlatforms',
  WINDSHIELD: 'windshield',
};

export function AdvertisementContent() {
  const {id: vehicleId} = useRequiredParams();
  const previewDialog = useAdvertisementPreviewDialog();
  const {resetPollingInterval, isMagicPublishingActive, setActiveMagicRequestId} =
    useAdvertisementPolling();
  const {isMandatoryFieldsEnabled, isLoading: isLoadingMandatoryFields} = useMandatoryFields();
  const {handleMandatoryFieldsError, isMandatoryFieldsError} =
    useMandatoryFieldsErrorDialog(vehicleId);
  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: vehicleId,
  });

  const {data: vehicle} = useGetVehicleQuery({vehicleId});

  const {isAdvertisingEnabled} = useIsLicenceEnabled();
  const isAdPlatformsTabHidden = !isAdvertisingEnabled;

  const [
    hasGenerateWindshieldPaperPermission,
    hasUpdateVehicleOnAllPlatformPermission,
    hasEnableVehicleInAdvertisementPermission,
    hasDisableVehicleInAdvertisementPermission,
    hasReadWindsheildPaperDocumentPermission,
    hasReadAdvertisingSettingsPermission,
  ] = usePermissions({
    permissionKeys: [
      'generateWindshieldPaper',
      'updateVehicleOnAllPlatform',
      'enableVehicleInAdvertisement',
      'disableVehicleInAdvertisement',
      'readWindsheildPaperDocument',
      'readAdvertisingSettings',
    ],
    scopes: {
      generateWindshieldPaper: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      updateVehicleOnAllPlatform: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      enableVehicleInAdvertisement: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      disableVehicleInAdvertisement: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      readWindsheildPaperDocument: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      readAdvertisingSettings: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const [tabId, setActiveTabId] = useQueryState(queryParams.VEHICLE_ADVERTISEMENT_PUBLISHING);
  const activeTabId = isAdPlatformsTabHidden ? TAB_IDS.WINDSHIELD : (tabId ?? TAB_IDS.AD_PLATFORMS);

  const {runPkwEnvkvPreflight, isChecking: isCheckingPkwEnvkv} = usePkwEnvkvAdvertisingPreflight();
  const [updateVehicleOnAllPlatforms, {isLoading: isUpdatingOnAllPlatforms}] =
    useUpdateVehicleOnAllPlatformsMutation();
  const [updateVehicleIsInSale, {isLoading: isUpdatingVehicleIsInSale}] =
    useUpdateVehicleIsInSaleMutation();
  const [validateMagicPublishing, {isLoading: isValidatingMagicPublishing}] =
    useValidateMagicPublishingMutation();
  const [renderWindshieldPaperDocument] = useRenderWindshieldPaperDocumentMutation();
  const {data: platforms} = useGetSalesPlatformsQuery({vehicleId});

  const handleRequestCreated = (requestId: string) => {
    setActiveMagicRequestId(requestId);
  };

  const [getTemplatesByCode] = useDocumentTemplates();
  const {getDropdownComponent} = getTemplatesByCode('windshield-paper');

  const {data: generalSettings} = useGetGeneralSettingsQuery({vehicleId});
  const inAdvertisement = generalSettings?.inSale;

  const downloadTemplate = (templateId: string) => {
    renderWindshieldPaperDocument({
      windshieldPaperRequestBody: {
        templateId,
        vehicleId,
      },
    })
      .unwrap()
      .then((result) => {
        if (result?.url) {
          openInNewTab(result.url);
        }
      })
      .catch(handleApiError);
  };

  const printTemplate = (templateId: string) => {
    renderWindshieldPaperDocument({
      windshieldPaperRequestBody: {
        templateId,
        vehicleId,
      },
    })
      .unwrap()
      .then((result) => {
        if (result?.pdfUrl) {
          printPDF(result.pdfUrl);
        }
      })
      .catch(handleApiError);
  };

  // Publishing statuses of the platforms must not block the action, only the in-sale flag decides.
  const isUpdateShown = !!inAdvertisement;

  const executeUpdate = () => {
    resetPollingInterval();
    return updateVehicleOnAllPlatforms({vehicleId})
      .unwrap()
      .then(() => true)
      .catch((error: ApiError) => {
        handleApiError(error);
        return false;
      });
  };

  const handleUpdate = () => {
    const descriptionAttribute = generalSettings?.general?.attributes?.find(
      (attr): attr is CommonDescriptionAttribute => attr.code === 'description'
    );
    const descriptionHtml = descriptionAttribute?.value ?? null;
    const overLimitPlatforms = getOverLimitPlatforms(descriptionHtml, platforms ?? []);

    if (overLimitPlatforms.length > 0) {
      openConfirmDialog({
        text: `${i18n.t('page.advertisement.labels.characterLimitExceeded')}\n\n${formatOverLimitWarnings(overLimitPlatforms)}`,
        // The confirmation can stay open for a while: re-check the vehicle first.
        onConfirm: () => runPkwEnvkvPreflight(vehicleId, executeUpdate),
      });
      return;
    }

    executeUpdate();
  };

  const executePublish = () => {
    if (isLoadingMandatoryFields && !inAdvertisement) {
      return Promise.resolve(false);
    }

    resetPollingInterval();
    const condition = ConditionEnumSchema.safeParse(vehicle?.vehicle?.condition);
    return updateVehicleIsInSale({
      vehicleId,
      publish: !inAdvertisement,
      validateMandatoryFields: isMandatoryFieldsEnabled(
        condition.success ? condition.data : undefined,
        getFieldAvailabilityContextFromVehicle(vehicle)
      ),
      publishMobileDeViaAdvertisingBridge:
        !inAdvertisement && isFeatureEnabled(featureFlags.ADVERTISEMENT_MOBILE_DE_PROMPT_BRIDGE),
      publishVgpViaAdvertisingBridge:
        !inAdvertisement && isFeatureEnabled(featureFlags.ADVERTISEMENT_VGP_PROMPT_BRIDGE),
      publishOtomotoViaAdvertisingBridge:
        !inAdvertisement && isFeatureEnabled(featureFlags.ADVERTISEMENT_OTOMOTO_PROMPT_BRIDGE),
      publishAutocarisViaAdvertisingBridge:
        !inAdvertisement && isFeatureEnabled(featureFlags.ADVERTISEMENT_AUTOCARIS_PROMPT_BRIDGE),
      publishMercedesNaSkladeViaAdvertisingBridge:
        !inAdvertisement &&
        isFeatureEnabled(featureFlags.ADVERTISEMENT_MERCEDES_NA_SKLADE_PROMPT_BRIDGE),
      publishHyundaiPromiseViaAdvertisingBridge:
        !inAdvertisement &&
        isFeatureEnabled(featureFlags.ADVERTISEMENT_HYUNDAI_PROMISE_PROMPT_BRIDGE),
      publishAutoplacPlViaAdvertisingBridge:
        !inAdvertisement && isFeatureEnabled(featureFlags.ADVERTISEMENT_AUTOPLAC_PL_PROMPT_BRIDGE),
    })
      .unwrap()
      .then(() => true)
      .catch((error: ApiError) => {
        if (isMandatoryFieldsError(error)) {
          handleMandatoryFieldsError(error.data?.validationErrors ?? []);
          return false;
        }

        handleApiError(error);
        return false;
      });
  };

  const previewArgs = useAdvertisementPreviewQueryArgs(vehicleId, inAdvertisement);
  const canPreviewAdvertisement = hasReadAdvertisingSettingsPermission;
  const confirmBeforeOpen = useConfirmUnsavedGeneralChangesBeforeOpen();
  const handlePreview = (initialPlatformCode?: AdvertisingPlatformCodeEnum) => {
    if (previewArgs === skipToken) {
      return;
    }

    confirmBeforeOpen(
      () => previewDialog.open(initialPlatformCode),
      testIds.vehicles.publishing('advertisement-preview-confirm-unsaved-general-changes')
    );
  };

  const handlePublish = () => {
    if (inAdvertisement) {
      // Unpublish never goes through the EnVKV preflight.
      executePublish();
      return;
    }

    const descriptionAttribute = generalSettings?.general?.attributes?.find(
      (attr): attr is CommonDescriptionAttribute => attr.code === 'description'
    );
    const descriptionHtml = descriptionAttribute?.value ?? null;

    const overLimitPlatforms = getOverLimitPlatforms(descriptionHtml, platforms ?? []);

    if (overLimitPlatforms.length > 0) {
      openConfirmDialog({
        text: `${i18n.t('page.advertisement.labels.characterLimitExceeded')}\n\n${formatOverLimitWarnings(overLimitPlatforms)}`,
        // The confirmation can stay open for a while: re-check the vehicle first.
        onConfirm: () => runPkwEnvkvPreflight(vehicleId, executePublish),
      });
      return;
    }

    executePublish();
  };

  const handleMagicPublishing = () => {
    validateMagicPublishing({vehicleId})
      .unwrap()
      .then((result) => {
        const descriptionAttribute = generalSettings?.general?.attributes?.find(
          (attr): attr is CommonDescriptionAttribute => attr.code === 'description'
        );
        const descriptionHtml = descriptionAttribute?.value ?? null;

        openMagicPublishingDialog({
          vehicleId,
          missingFields: result.missingFields,
          missingMandatoryFields: result.missingMandatoryFields,
          hasSalesPhotos: result.hasSalesPhotos,
          overLimitPlatforms: getOverLimitPlatforms(descriptionHtml, platforms ?? []),
          onRequestCreated: handleRequestCreated,
          'data-testid': testIds.vehicles.publishing('magicPublishingDialog'),
        });
      })
      .catch(handleApiError);
  };

  const platformActions = buildArray<Action>()
    .when(isFeatureEnabled(featureFlags.MAGIC_BUTTON) && !inAdvertisement, {
      title: i18n.t('entity.vehicle.labels.magicPublishing'),
      onClick: () => void runPkwEnvkvPreflight(vehicleId, handleMagicPublishing),
      isLoading: isValidatingMagicPublishing || isMagicPublishingActive || isCheckingPkwEnvkv,
      type: 'button',
      variant: 'outlined',
      leftIcon: 'action/magic_wand',
      'data-testid': testIds.vehicles.publishing('magicButton'),
    })
    .when(canPreviewAdvertisement, {
      title: i18n.t('general.actions.preview'),
      onClick: () => handlePreview(),
      type: 'button',
      variant: 'outlined',
      leftIcon: 'action/visibility',
      'data-testid': testIds.vehicles.publishing('preview'),
    })
    .when(hasUpdateVehicleOnAllPlatformPermission && isUpdateShown, {
      title: i18n.t('page.advertisement.actions.updateAd'),
      onClick: () => void runPkwEnvkvPreflight(vehicleId, handleUpdate),
      isLoading: isUpdatingOnAllPlatforms || isCheckingPkwEnvkv,
      type: 'button',
      variant: 'outlined',
      'data-testid': testIds.vehicles.publishing('refresh'),
    })
    .when(hasDisableVehicleInAdvertisementPermission && inAdvertisement, {
      title: i18n.t('page.advertisement.actions.unPublish'),
      onClick: handlePublish,
      isLoading: isUpdatingVehicleIsInSale,
      type: 'button',
      variant: 'danger',
      'data-testid': testIds.vehicles.publishing('publish'),
    })
    .when(hasEnableVehicleInAdvertisementPermission && !inAdvertisement, {
      title: i18n.t('page.advertisement.actions.publish'),
      onClick: () => void runPkwEnvkvPreflight(vehicleId, handlePublish),
      isLoading: isUpdatingVehicleIsInSale || isCheckingPkwEnvkv,
      isDisabled: isLoadingMandatoryFields,
      type: 'button',
      variant: 'primary',
      'data-testid': testIds.vehicles.publishing('publish'),
    });

  const tabs = buildArray<TabProps>()
    .whenNot(isAdPlatformsTabHidden, {
      id: TAB_IDS.AD_PLATFORMS,
      'data-testid': testIds.vehicles.publishing('adPlatforms'),
      title: i18n.t('entity.vehicle.labels.advertisementTabPlatforms'),
      content: (
        <AdvertisementTab
          isPollingEnabled={activeTabId === TAB_IDS.AD_PLATFORMS}
          canPreview={canPreviewAdvertisement}
          onPreview={handlePreview}
          data-testid={testIds.vehicles.publishing('adPlatformsContent')}
        />
      ),
      actions: platformActions,
    })
    .add({
      id: TAB_IDS.WINDSHIELD,
      'data-testid': testIds.vehicles.publishing('windshield'),
      title: i18n.t('entity.vehicle.labels.windshieldTab'),
      content: <WindshieldTab data-testid={testIds.vehicles.publishing('windshieldContent')} />,
      actions: buildArray<Action>()
        .when(hasGenerateWindshieldPaperPermission, {
          type: 'custom',
          component: getDropdownComponent({
            title: i18n.t('page.advertisement.labels.downloadWindshield'),
            isDisabled: !hasAclDocsWithPermission(hasReadWindsheildPaperDocumentPermission),
            onChange: downloadTemplate,
          }),
        })
        .when(hasGenerateWindshieldPaperPermission, {
          type: 'custom',
          component: getDropdownComponent({
            title: i18n.t('page.advertisement.labels.printWindshield'),
            isDisabled: !hasAclDocsWithPermission(hasReadWindsheildPaperDocumentPermission),
            onChange: printTemplate,
          }),
        }),
    });

  return (
    <VStack spacing={4}>
      <Show when={isAdvertisingEnabled && activeTabId === TAB_IDS.AD_PLATFORMS}>
        <PkwEnvkvAdvertisingNotice vehicleId={vehicleId} />
      </Show>
      <Tabs
        variant="condensed"
        onChange={setActiveTabId}
        activeTabId={activeTabId}
        tabs={tabs}
        data-testid={testIds.vehicles.publishing('tabs')}
      />
      <AdvertisementPreviewDialog
        isOpen={
          previewDialog.isOpen &&
          canPreviewAdvertisement &&
          isAdvertisingEnabled &&
          previewArgs !== skipToken
        }
        vehicleId={vehicleId}
        initialPlatformCode={previewDialog.initialPlatformCode}
        inAdvertisement={!!inAdvertisement}
        hasPublishPermission={hasEnableVehicleInAdvertisementPermission}
        hasUpdatePermission={hasUpdateVehicleOnAllPlatformPermission}
        isPublishBlocked={isLoadingMandatoryFields}
        onPublish={executePublish}
        onUpdate={executeUpdate}
        onClose={previewDialog.close}
        data-testid={testIds.vehicles.publishing('advertisementPreviewDialog')}
      />
    </VStack>
  );
}
